import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from 'react-i18next'

const T = (props) => <Input {...props} className="mt-1 rounded-sm focus-visible:ring-1 focus-visible:ring-[#C5A059]" />;

export default function ContactForm({ open, onOpenChange, meta, initial, onSubmit }) {
  const { t } = useTranslation()
  const [form, setForm] = useState(
    initial || {
      name: "", company: "", position: "", contact_type: "Buyer", email: "", secondary_email: "",
      mobile: "", whatsapp: "", office: "", website: "", linkedin: "", address: "", country: "Portugal",
      language: "pt", investment_profile: "", preferred_locations: "", preferred_property_types: "",
      min_budget: "", max_budget: "", expected_roi: "", required_area: "", required_rooms: "",
      source: "", notes: "", gdpr_consent: false, next_follow_up: "",
    }
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const numFields = ["min_budget", "max_budget", "expected_roi", "required_area", "required_rooms"];

  const submit = () => {
    const payload = { ...form };
    numFields.forEach((k) => {
      if (payload[k] === "" || payload[k] == null) delete payload[k];
      else payload[k] = Number(payload[k]);
    });
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{initial ? t('editContact', 'Edit Contact') : t('addContact2', 'Add Contact')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t('fullName', 'Full Name *')}</Label><T data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div>
              <Label>{t('contactType', 'Contact Type')}</Label>
              <Select value={form.contact_type} onValueChange={(v) => set("contact_type", v)}>
                <SelectTrigger data-testid="contact-type" className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {meta?.contact_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t('company', 'Company')}</Label><T value={form.company} onChange={(e) => set("company", e.target.value)} /></div>
            <div><Label>{t('position', 'Position')}</Label><T value={form.position} onChange={(e) => set("position", e.target.value)} /></div>
            <div><Label>{t('email', 'Email')}</Label><T data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label>{t('mobile', 'Mobile')}</Label><T value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
            <div><Label>{t('whatsapp', 'WhatsApp')}</Label><T value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></div>
            <div><Label>{t('website', 'Website')}</Label><T value={form.website} onChange={(e) => set("website", e.target.value)} /></div>
            <div><Label>{t('country', 'Country')}</Label><T value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
            <div>
              <Label>{t('preferredLanguage', 'Preferred Language')}</Label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="pt">{t('portugus', 'Português')}</SelectItem>
                  <SelectItem value="en">{t('english', 'English')}</SelectItem>
                  <SelectItem value="fr">{t('franais', 'Français')}</SelectItem>
                  <SelectItem value="es">{t('espaol', 'Español')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{t('investmentProfile', 'Investment Profile')}</div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t('profile', 'Profile')}</Label><T value={form.investment_profile} onChange={(e) => set("investment_profile", e.target.value)} placeholder={t('egValueadd', 'e.g. Value-add')} /></div>
            <div><Label>{t('source', 'Source')}</Label><T value={form.source} onChange={(e) => set("source", e.target.value)} /></div>
            <div><Label>{t('preferredLocations', 'Preferred Locations')}</Label><T value={form.preferred_locations} onChange={(e) => set("preferred_locations", e.target.value)} placeholder={t('bragaPorto', 'Braga, Porto')} /></div>
            <div><Label>{t('preferredTypes', 'Preferred Types')}</Label><T value={form.preferred_property_types} onChange={(e) => set("preferred_property_types", e.target.value)} placeholder={t('villaHotel', 'Villa, Hotel')} /></div>
            <div><Label>{t('minBudget', 'Min Budget (€)')}</Label><T type="number" value={form.min_budget} onChange={(e) => set("min_budget", e.target.value)} /></div>
            <div><Label>{t('maxBudget', 'Max Budget (€)')}</Label><T type="number" value={form.max_budget} onChange={(e) => set("max_budget", e.target.value)} /></div>
            <div><Label>{t('expectedRoi', 'Expected ROI %')}</Label><T type="number" value={form.expected_roi} onChange={(e) => set("expected_roi", e.target.value)} /></div>
            <div><Label>{t('requiredRooms', 'Required Rooms')}</Label><T type="number" value={form.required_rooms} onChange={(e) => set("required_rooms", e.target.value)} /></div>
            <div><Label>{t('nextFollowup', 'Next Follow-up')}</Label><T type="date" value={(form.next_follow_up || "").slice(0, 10)} onChange={(e) => set("next_follow_up", e.target.value)} /></div>
          </div>
          <div><Label>{t('notes', 'Notes')}</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={!!form.gdpr_consent} onCheckedChange={(v) => set("gdpr_consent", v)} /> {t('gdprConsentObtained', 'GDPR consent obtained')}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>{t('cancel', 'Cancel')}</Button>
          <Button data-testid="contact-save" disabled={!form.name} onClick={submit} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
            {initial ? t('saveChanges', 'Save changes') : t('createContact', 'Create contact')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
